// Add this component for mobile element selection
// src/components/MobileElementSelector.tsx
import React, { useState } from 'react';
import { useElementStore } from '../store/elementStore';
import ElementCanvas from './ElementCanvas';

interface MobileElementSelectorProps {
  onSelect: (elementId: string) => void;
  onClose: () => void;
  currentSlot: number;
}

const MobileElementSelector: React.FC<MobileElementSelectorProps> = ({
  onSelect,
  onClose,
  currentSlot
}) => {
  const { all, unlocked } = useElementStore();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter elements that are unlocked
  const availableElements = Object.values(all)
    .filter(e => unlocked.has(e.id))
    .filter(e => !searchTerm || e.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier;
      return a.name.localeCompare(b.name);
    });
  
  const handleElementSelect = (id: string) => {
    onSelect(id);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-75 flex flex-col">
      <div className="bg-gray-800 p-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Select Element for Slot {currentSlot + 1}</h3>
        <button
          onClick={onClose}
          className="text-white text-2xl hover:text-gray-300 transition-colors"
        >
          ×
        </button>
      </div>

      <div className="p-4 bg-gray-800 border-t border-gray-700">
        <input
          type="text"
          placeholder="Search elements..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-700 text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex-1 bg-gray-900 overflow-y-auto p-4 md:p-6 pb-20">
        <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 pb-8">
          {availableElements.map(element => (
            <div
              key={element.id}
              className="flex flex-col items-center p-2 bg-gray-800 rounded-md active:bg-gray-700 hover:bg-gray-700 cursor-pointer transition-colors"
              onClick={() => handleElementSelect(element.id)}
            >
              <ElementCanvas e={element} size={48} />
              <div className="mt-1 text-xs text-center text-white">{element.name}</div>
              <div className="text-xs text-gray-400">Tier {element.tier}</div>
            </div>
          ))}

          {availableElements.length === 0 && (
            <div className="col-span-4 md:col-span-5 lg:col-span-6 text-center py-8 text-gray-400">
              No elements match your search
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileElementSelector;